<template>
  <div class="media-page page-ind-kpi min-h-screen w-full p-3 md:p-4">

    <!-- ── Hero ── -->
    <section class="usr-hero mb-4">
      <div class="usr-hero-text">
        <h2 class="usr-hero-title">
          <i class="pi pi-users usr-hero-icon"></i>
          Usuarios
        </h2>
        <p class="usr-hero-subtitle">
          Alta, baja y permisos del personal que opera el panel. Solo usuarios autorizados pueden modificar estas cuentas.
        </p>
      </div>
      <div class="usr-hero-stats">
        <div class="usr-hero-stat">
          <span class="usr-hero-stat-value">{{ users.length }}</span>
          <span class="usr-hero-stat-label">Cuentas activas</span>
        </div>
        <div class="usr-hero-stat">
          <span class="usr-hero-stat-value">{{ adminCount }}</span>
          <span class="usr-hero-stat-label">Administradores</span>
        </div>
        <div class="usr-hero-stat">
          <span class="usr-hero-stat-value">{{ panelCount }}</span>
          <span class="usr-hero-stat-label">Con acceso al panel</span>
        </div>
      </div>
    </section>

    <!-- ── Toolbar ── -->
    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-left">
        <div class="ind-toolbar-field">
          <label class="field-label block mb-1">Buscar</label>
          <div class="kpi-inline-controls-search-input">
            <i class="pi pi-search usr-search-icon"></i>
            <InputText
              v-model="searchQuery"
              placeholder="Nombre o usuario…"
              class="usr-search-input kpi-table-search kpi-table-search-with-icon"
            />
          </div>
        </div>
        <div class="usr-filters">
          <button
            v-for="opt in filterOptions"
            :key="opt.value"
            type="button"
            class="usr-filter-chip"
            :class="{ 'is-active': filterKey === opt.value }"
            @click="filterKey = opt.value"
          >
            <i v-if="opt.icon" :class="opt.icon"></i>
            {{ opt.label }}
            <span class="usr-filter-count">{{ opt.count }}</span>
          </button>
        </div>
      </div>
      <div class="ind-toolbar-right">
        <Button
          label="Nuevo usuario"
          icon="pi pi-plus"
          class="p-button-success p-button-sm"
          @click="openNew"
        />
      </div>
    </section>

    <div class="kpi-page-content">
      <!-- ── Cargando (skeleton) ── -->
      <section v-if="loading" class="kpi-section mb-5 kpi-skel-fade-in">
        <div class="etq-skel">
          <div v-for="n in 8" :key="n" class="etq-skel-row">
            <div class="etq-skel-cell w-1/4"></div>
            <div class="etq-skel-cell w-1/5"></div>
            <div class="etq-skel-cell w-1/6"></div>
            <div class="etq-skel-cell w-1/6"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
        </div>
      </section>

      <!-- ── Vacío ── -->
      <section v-else-if="users.length === 0" class="kpi-section mb-5">
        <div class="etq-empty">
          <i class="pi pi-users etq-empty-icon"></i>
          <p class="etq-empty-title">No hay usuarios registrados</p>
          <p class="etq-empty-subtitle">Crea la primera cuenta para que el personal pueda iniciar sesión en el panel.</p>
          <Button label="Crear primer usuario" icon="pi pi-plus" class="p-button-sm" @click="openNew" />
        </div>
      </section>

      <!-- ── Sin resultados ── -->
      <section v-else-if="filteredUsers.length === 0" class="kpi-section mb-5">
        <div class="etq-empty">
          <i class="pi pi-search etq-empty-icon etq-empty-icon--sm"></i>
          <p class="etq-empty-title">Sin resultados</p>
          <p class="etq-empty-subtitle">
            No se encontraron usuarios que coincidan con
            <template v-if="searchQuery">“{{ searchQuery }}”</template>
            <template v-else>el filtro seleccionado</template>.
          </p>
          <Button label="Limpiar filtros" icon="pi pi-times" class="p-button-sm p-button-outlined"
            @click="searchQuery = ''; filterKey = 'all'" />
        </div>
      </section>

      <!-- ── Tabla ── -->
      <section v-else class="kpi-section mb-5">
        <h3 class="etq-section-title">
          <i class="pi pi-users etq-section-icon"></i>
          Cuentas de usuario
        </h3>

        <div class="etq-table-count">
          <span>{{ filteredUsers.length }} usuario{{ filteredUsers.length !== 1 ? 's' : '' }}</span>
        </div>

        <DataTable
          :value="filteredUsers"
          stripedRows
          responsiveLayout="scroll"
          class="text-sm kpi-spots-table"
          :paginator="filteredUsers.length > 15"
          :rows="15"
          :rowsPerPageOptions="[15, 30, 50]"
          :rowHover="true"
          @row-click="onRowClick"
        >
          <Column field="name" header="Nombre" :sortable="true" style="min-width:220px">
            <template #body="{ data: row }">
              <div class="usr-cell-name">
                <span class="usr-avatar">{{ initials(row.name) }}</span>
                <div>
                  <div class="usr-name">{{ row.name }}</div>
                  <div class="usr-username">{{ row.username }}</div>
                </div>
              </div>
            </template>
          </Column>
          <Column header="Tipo" style="width:170px">
            <template #body="{ data: row }">
              <span v-if="row.is_admin" class="usr-badge usr-badge-admin">
                <i class="pi pi-shield"></i> Administrador
              </span>
              <span v-else-if="row.is_panel" class="usr-badge usr-badge-panel">
                <i class="pi pi-user"></i> Panel
              </span>
              <span v-else-if="row.has_booth" class="usr-badge usr-badge-booth">
                <i class="pi pi-microphone"></i> Cabina
              </span>
              <span v-else class="usr-badge usr-badge-external">
                <i class="pi pi-user-minus"></i> Sin acceso
              </span>
            </template>
          </Column>
          <Column header="Permisos" style="width:120px">
            <template #body="{ data: row }">
              <span class="usr-perm-count">{{ row.permission_count }}</span>
              <span class="usr-perm-label">permiso{{ row.permission_count !== 1 ? 's' : '' }}</span>
            </template>
          </Column>
          <Column field="last_activity" header="Última actividad" :sortable="true" style="width:170px">
            <template #body="{ data: row }">
              <span class="etq-cell-date">{{ row.last_activity || '—' }}</span>
            </template>
          </Column>
          <Column header="" style="width:110px">
            <template #body="{ data: row }">
              <div class="etq-cell-actions">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button-sm etq-btn-icon etq-btn-edit"
                  @click.stop="openEdit(row)"
                  v-tooltip.top="'Editar usuario'"
                />
                <Button
                  icon="pi pi-key"
                  class="p-button-rounded p-button-text p-button-sm etq-btn-icon usr-btn-key"
                  @click.stop="openPasswordDialog(row)"
                  v-tooltip.top="'Restablecer contraseña'"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-sm etq-btn-icon etq-btn-delete"
                  :disabled="row.id === capabilities.actor_id"
                  @click.stop="confirmDelete(row)"
                  v-tooltip.top="row.id === capabilities.actor_id ? 'No puedes eliminar tu propia cuenta' : 'Eliminar usuario'"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </section>
    </div>

    <!-- ── Formulario (Sidebar) ── -->
    <Sidebar
      v-model:visible="sidebarVisible"
      position="right"
      :dismissable="false"
      :showCloseIcon="false"
      :baseZIndex="1200"
      :style="drawerStyle"
      class="usr-sidebar"
    >
      <div class="usr-form">
        <header class="usr-form-header">
          <div>
            <h3 class="usr-form-title">{{ editingId ? 'Editar usuario' : 'Nuevo usuario' }}</h3>
            <p class="usr-form-subtitle">
              {{ editingId ? `Editando la cuenta de ${formName || '—'}` : 'Completa los datos para crear la cuenta' }}
            </p>
          </div>
          <button type="button" class="usr-form-close" @click="closeForm">
            <i class="pi pi-times"></i>
          </button>
        </header>

        <div class="usr-form-body">
          <!-- Datos generales -->
          <section class="usr-form-section">
            <h4 class="usr-form-section-title">
              <i class="pi pi-id-card"></i>
              Datos generales
            </h4>
            <div class="usr-form-grid">
              <div class="usr-field">
                <label class="usr-label">Nombre completo</label>
                <InputText v-model="formName" class="w-full" maxlength="120" placeholder="Ej: Juan Pérez" />
              </div>
              <div class="usr-field">
                <label class="usr-label">Usuario</label>
                <InputText v-model="formUsername" class="w-full" maxlength="100" placeholder="Ej: jperez" />
                <p class="usr-hint">Clave con la que inicia sesión. Se usa en el panel viejo y nuevo.</p>
              </div>
              <div class="usr-field">
                <label class="usr-label">
                  Contraseña
                  <span v-if="editingId" class="usr-optional">(opcional)</span>
                </label>
                <Password
                  v-model="formPassword"
                  class="w-full"
                  :feedback="true"
                  :toggleMask="true"
                  :placeholder="editingId ? 'Dejar en blanco para no cambiar' : 'Mínimo 8 caracteres'"
                  inputClass="w-full"
                />
                <p class="usr-hint" v-if="editingId">Si se captura, se cerrarán las sesiones abiertas de este usuario.</p>
              </div>
              <div class="usr-field">
                <label class="usr-label">Repetir contraseña</label>
                <Password
                  v-model="formPasswordConfirm"
                  class="w-full"
                  :feedback="false"
                  :toggleMask="true"
                  :disabled="!formPassword"
                  placeholder="Confirma la contraseña"
                  inputClass="w-full"
                />
              </div>
            </div>
          </section>

          <!-- Finanzas -->
          <section class="usr-form-section">
            <h4 class="usr-form-section-title">
              <i class="pi pi-wallet"></i>
              Finanzas
            </h4>
            <div class="usr-form-grid">
              <div class="usr-field usr-field-inline">
                <InputSwitch v-model="formFinanceDefault" inputId="usrFinanceDefault" />
                <label for="usrFinanceDefault" class="usr-label-inline">
                  Abrir finanzas por defecto
                  <span class="usr-hint">El usuario entra directo al módulo financiero.</span>
                </label>
              </div>
              <div class="usr-field">
                <label class="usr-label">Sucursal de finanzas</label>
                <Dropdown
                  v-model="formFinanceBranchId"
                  :options="branches"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Selecciona una sucursal"
                  class="w-full"
                />
              </div>
            </div>
          </section>

          <!-- Permisos -->
          <section class="usr-form-section">
            <div class="usr-form-section-head">
              <h4 class="usr-form-section-title">
                <i class="pi pi-lock"></i>
                Permisos del panel
              </h4>
              <div class="usr-perm-search">
                <i class="pi pi-search"></i>
                <InputText v-model="permissionSearch" placeholder="Buscar permiso…" class="usr-perm-search-input" />
              </div>
            </div>

            <p class="usr-hint mb-2">
              {{ formPermissions.length }} permiso{{ formPermissions.length !== 1 ? 's' : '' }} seleccionado{{ formPermissions.length !== 1 ? 's' : '' }}.
            </p>

            <div
              v-for="group in visiblePermissionGroups"
              :key="group.key"
              class="usr-perm-group"
              :class="{ 'is-locked': group.adminOnly && !capabilities.actor_is_admin }"
            >
              <div class="usr-perm-group-head">
                <span class="usr-perm-group-title">{{ group.label }}</span>
                <span class="usr-perm-group-actions">
                  <span class="usr-perm-group-count">{{ selectedInGroup(group) }}/{{ group.permissions.length }}</span>
                  <button
                    type="button"
                    class="usr-link"
                    :disabled="group.adminOnly && !capabilities.actor_is_admin"
                    @click="setGroup(group, true)"
                  >Todo</button>
                  <button
                    type="button"
                    class="usr-link"
                    :disabled="group.adminOnly && !capabilities.actor_is_admin"
                    @click="setGroup(group, false)"
                  >Ninguno</button>
                </span>
              </div>
              <div class="usr-perm-grid">
                <div
                  v-for="perm in group.permissions"
                  :key="perm.code"
                  class="usr-perm-item"
                >
                  <Checkbox
                    v-model="formPermissions"
                    :value="perm.code"
                    :inputId="`perm-${perm.code}`"
                    :disabled="group.adminOnly && !capabilities.actor_is_admin"
                  />
                  <label :for="`perm-${perm.code}`" class="usr-perm-label-text">{{ perm.label }}</label>
                </div>
              </div>
              <p v-if="group.adminOnly && !capabilities.actor_is_admin" class="usr-hint">
                Solo un administrador puede otorgar estos permisos.
              </p>
            </div>
          </section>

          <!-- Cabina -->
          <section class="usr-form-section">
            <h4 class="usr-form-section-title">
              <i class="pi pi-microphone"></i>
              Cabina Vybroo
            </h4>
            <div class="usr-field usr-field-inline mb-2">
              <InputSwitch v-model="boothEnabled" inputId="usrBoothEnabled" />
              <label for="usrBoothEnabled" class="usr-label-inline">
                Habilitar acceso a cabina
                <span class="usr-hint">Permite operar canales desde la cabina del panel.</span>
              </label>
            </div>

            <template v-if="boothEnabled">
              <div class="usr-perm-search mb-2">
                <i class="pi pi-search"></i>
                <InputText v-model="radioSearch" placeholder="Buscar canal…" class="usr-perm-search-input" />
              </div>
              <div class="usr-booth-toolbar">
                <span class="usr-hint">{{ formBoothRadios.length }} de {{ filteredRadios.length }} canal{{ filteredRadios.length !== 1 ? 'es' : '' }}</span>
                <span class="usr-booth-actions">
                  <button type="button" class="usr-link" @click="setBoothAll(true)">Todos</button>
                  <button type="button" class="usr-link" @click="setBoothAll(false)">Ninguno</button>
                </span>
              </div>
              <div class="usr-booth-grid">
              <div
                  v-for="radio in filteredRadios"
                  :key="radio.id"
                  class="usr-booth-item"
                >
                  <Checkbox
                    v-model="formBoothRadios"
                    :value="radio.id"
                    :inputId="`booth-${radio.id}`"
                  />
                  <label :for="`booth-${radio.id}`" class="usr-booth-name">{{ radio.name }}</label>
                </div>
              </div>
            </template>
          </section>
        </div>

        <footer class="usr-form-footer">
          <Button
            v-if="editingId && editingId !== capabilities.actor_id"
            label="Eliminar"
            icon="pi pi-trash"
            class="p-button-danger p-button-text p-button-sm"
            @click="confirmDeleteById"
          />
          <span class="usr-form-footer-spacer"></span>
          <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="closeForm" :disabled="saving" />
          <Button
            :label="editingId ? 'Guardar cambios' : 'Crear usuario'"
            icon="pi pi-check"
            class="p-button-success"
            :loading="saving"
            :disabled="saving || !canSave"
            @click="saveUser"
          />
        </footer>
      </div>
    </Sidebar>

    <!-- ── Dialog reset contraseña ── -->
    <Dialog
      v-model:visible="passwordDialogVisible"
      modal
      header="Restablecer contraseña"
      :style="{ width: '420px' }"
      :dismissableMask="true"
      class="etq-dialog"
    >
      <div class="etq-dialog-body">
        <p class="usr-hint">
          Se asignará una contraseña nueva a
          <strong>{{ passwordUserName }}</strong>
          y se cerrarán sus sesiones abiertas.
        </p>
        <div class="etq-dialog-field">
          <label class="etq-dialog-label">Nueva contraseña</label>
          <Password v-model="passwordForm" class="w-full" :feedback="true" :toggleMask="true" placeholder="Mínimo 8 caracteres" inputClass="w-full" />
        </div>
        <div class="etq-dialog-field">
          <label class="etq-dialog-label">Repetir contraseña</label>
          <Password v-model="passwordFormConfirm" class="w-full" :feedback="false" :toggleMask="true" placeholder="Confirma la contraseña" inputClass="w-full" />
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="passwordDialogVisible = false" :disabled="saving" />
        <Button
          label="Restablecer"
          icon="pi pi-key"
          class="p-button-success"
          :loading="saving"
          :disabled="saving || !canResetPassword"
          @click="resetPassword"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import axiosInstance from '@/axiosInstance';
import Password from 'primevue/password';

export default {
  name: 'Usuarios',
  emits: ['update-loading'],
  components: { Password },

  data() {
    return {
      users: [],
      loading: false,
      saving: false,
      searchQuery: '',
      filterKey: 'all',

      sidebarVisible: false,
      editingId: null,
      formName: '',
      formUsername: '',
      formPassword: '',
      formPasswordConfirm: '',
      formFinanceDefault: false,
      formFinanceBranchId: -1,

      permissionGroups: [],
      formPermissions: [],
      permissionSearch: '',

      boothEnabled: false,
      formBoothRadios: [],
      radioSearch: '',
      radios: [],
      branches: [],
      capabilities: { actor_id: 0, actor_is_admin: true, is_self: false, can_manage_admin: true },

      passwordDialogVisible: false,
      passwordUserId: null,
      passwordUserName: '',
      passwordForm: '',
      passwordFormConfirm: '',
    };
  },

  computed: {
    drawerStyle() {
      return { width: '880px', maxWidth: '96vw' };
    },

    adminCount() {
      return this.users.filter((u) => u.is_admin).length;
    },
    panelCount() {
      return this.users.filter((u) => u.is_panel).length;
    },

    filterOptions() {
      const all = this.users;
      return [
        { value: 'all', label: 'Todos', count: all.length, icon: '' },
        { value: 'panel', label: 'Panel', count: all.filter((u) => u.is_panel).length, icon: 'pi pi-user' },
        { value: 'booth', label: 'Cabina', count: all.filter((u) => u.has_booth).length, icon: 'pi pi-microphone' },
        { value: 'admin', label: 'Administradores', count: all.filter((u) => u.is_admin).length, icon: 'pi pi-shield' },
        { value: 'external', label: 'Sin acceso', count: all.filter((u) => !u.is_panel && !u.has_booth).length, icon: 'pi pi-user-minus' },
      ];
    },

    filteredUsers() {
      let result = this.users;
      switch (this.filterKey) {
        case 'panel':
          result = result.filter((u) => u.is_panel);
          break;
        case 'booth':
          result = result.filter((u) => u.has_booth);
          break;
        case 'admin':
          result = result.filter((u) => u.is_admin);
          break;
        case 'external':
          result = result.filter((u) => !u.is_panel && !u.has_booth);
          break;
      }
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        result = result.filter((u) =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.username || '').toLowerCase().includes(q)
        );
      }
      return result;
    },

    visiblePermissionGroups() {
      const q = this.permissionSearch.trim().toLowerCase();
      return this.permissionGroups
        .map((group) => ({
          ...group,
          permissions: q
            ? group.permissions.filter(
                (p) =>
                  p.label.toLowerCase().includes(q) ||
                  p.code.toLowerCase().includes(q)
              )
            : group.permissions,
        }))
        .filter((group) => group.permissions.length > 0);
    },

    filteredRadios() {
      const q = this.radioSearch.trim().toLowerCase();
      if (!q) return this.radios;
      return this.radios.filter((r) => (r.name || '').toLowerCase().includes(q));
    },

    canSave() {
      if (!this.formName || this.formName.trim().length < 3) return false;
      if (!this.formUsername || this.formUsername.trim().length < 3) return false;
      if (this.editingId) {
        if (this.formPassword && (this.formPassword.length < 8 || this.formPassword !== this.formPasswordConfirm)) {
          return false;
        }
        return true;
      }
      return (
        !!this.formPassword &&
        this.formPassword.length >= 8 &&
        this.formPassword === this.formPasswordConfirm
      );
    },

    canResetPassword() {
      return (
        !!this.passwordForm &&
        this.passwordForm.length >= 8 &&
        this.passwordForm === this.passwordFormConfirm
      );
    },
  },

  methods: {
    initials(name) {
      const parts = String(name || '').trim().split(/\s+/);
      return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?';
    },

    async fetchUsers() {
      this.loading = true;
      try {
        const res = await axiosInstance.post('/api/admin_users_list', {
          token: localStorage.getItem('token'),
        });
        if (res.data?.success) {
          this.users = res.data.users || [];
        } else {
          this.showError(res.data?.message || 'No se pudo cargar la lista de usuarios.');
        }
      } catch (e) {
        console.error('Error al cargar usuarios:', e);
        this.showError('Error al cargar la lista de usuarios.');
      } finally {
        this.loading = false;
      }
    },

    showError(detail) {
      this.$toast.add({
        severity: 'error', summary: 'Error', detail, life: 5000, group: 'topr',
      });
    },

    showSuccess(detail) {
      this.$toast.add({
        severity: 'success', summary: 'Éxito', detail, life: 3500, group: 'topr',
      });
    },

    async loadForm(userId) {
      try {
        const res = await axiosInstance.post('/api/admin_user_show', {
          token: localStorage.getItem('token'),
          user_id: userId || 0,
        });
        if (res.data?.success) {
          this.permissionGroups = res.data.permission_groups || [];
          this.radios = res.data.radios || [];
          this.branches = res.data.branches || [];
          this.capabilities = res.data.capabilities || this.capabilities;

          const u = res.data.user;
          if (u) {
            this.editingId = u.id;
            this.formName = u.name;
            this.formUsername = u.username;
            this.formPassword = '';
            this.formPasswordConfirm = '';
            this.formFinanceDefault = !!u.finance_default;
            this.formFinanceBranchId = u.finance_branch_id ?? -1;
            this.formPermissions = (u.permission_codes || []).filter((c) => !String(c).startsWith('booth'));
            this.boothEnabled = !!u.booth_enabled;
            this.formBoothRadios = u.booth_radios || [];
          } else {
            this.resetFormFields();
          }
          return true;
        }
        this.showError(res.data?.message || 'No se pudo cargar el formulario.');
        return false;
      } catch (e) {
        console.error('Error al cargar formulario:', e);
        this.showError('Error al cargar el formulario.');
        return false;
      }
    },

    resetFormFields() {
      this.editingId = null;
      this.formName = '';
      this.formUsername = '';
      this.formPassword = '';
      this.formPasswordConfirm = '';
      this.formFinanceDefault = false;
      this.formFinanceBranchId = -1;
      this.formPermissions = [];
      this.boothEnabled = false;
      this.formBoothRadios = [];
      this.permissionSearch = '';
      this.radioSearch = '';
    },

    async openNew() {
      this.resetFormFields();
      const ok = await this.loadForm(0);
      if (ok) this.sidebarVisible = true;
    },

    async openEdit(row) {
      this.resetFormFields();
      const ok = await this.loadForm(row.id);
      if (ok) this.sidebarVisible = true;
    },

    onRowClick(event) {
      if (event && event.data) {
        this.openEdit(event.data);
      }
    },

    closeForm() {
      if (this.saving) return;
      this.sidebarVisible = false;
      this.resetFormFields();
    },

    selectedInGroup(group) {
      return group.permissions.filter((p) => this.formPermissions.includes(p.code)).length;
    },

    setGroup(group, value) {
      const codes = group.permissions.map((p) => p.code);
      if (value) {
        this.formPermissions = Array.from(new Set([...this.formPermissions, ...codes]));
      } else {
        this.formPermissions = this.formPermissions.filter((c) => !codes.includes(c));
      }
    },

    setBoothAll(value) {
      if (value) {
        this.formBoothRadios = this.filteredRadios.map((r) => r.id);
      } else {
        this.formBoothRadios = [];
      }
    },

    buildPermissionsPayload() {
      const perms = [...this.formPermissions];
      if (this.boothEnabled) {
        perms.push('booth');
        this.formBoothRadios.forEach((id) => perms.push(`booth${id}`));
      }
      return perms;
    },

    async saveUser() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        const payload = {
          token: localStorage.getItem('token'),
          name: this.formName.trim(),
          username: this.formUsername.trim(),
          finance_default: this.formFinanceDefault ? 1 : 0,
          finance_branch_id: this.formFinanceBranchId ?? -1,
          permissions: this.buildPermissionsPayload(),
        };
        if (this.editingId) {
          payload.id = this.editingId;
          if (this.formPassword) {
            payload.password = this.formPassword;
            payload.password_confirmation = this.formPasswordConfirm;
          }
        } else {
          payload.password = this.formPassword;
          payload.password_confirmation = this.formPasswordConfirm;
        }

        const res = await axiosInstance.post('/api/admin_user_save', payload);
        if (res.data?.success) {
          this.showSuccess(this.editingId ? 'Usuario actualizado correctamente.' : 'Usuario creado correctamente.');
          this.closeForm();
          await this.fetchUsers();
        } else {
          this.showError(res.data?.message || 'No se pudo guardar el usuario.');
        }
      } catch (e) {
        console.error('Error al guardar usuario:', e);
        this.showError(e?.response?.data?.message || 'Error al guardar el usuario.');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(row) {
      if (row.id === this.capabilities.actor_id) return;
      this.$confirm.require({
        header: 'Eliminar usuario',
        message: `¿Eliminar la cuenta de “${row.name}”? Esta acción revoca su acceso al panel.`,
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Cancelar',
        acceptLabel: 'Eliminar',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => this.deleteUser(row.id),
      });
    },

    confirmDeleteById() {
      const row = this.users.find((u) => u.id === this.editingId);
      if (row) this.confirmDelete(row);
    },

    async deleteUser(id) {
      this.saving = true;
      try {
        const res = await axiosInstance.post('/api/admin_user_delete', {
          token: localStorage.getItem('token'),
          id,
        });
        if (res.data?.success) {
          this.showSuccess('Usuario eliminado correctamente.');
          this.closeForm();
          await this.fetchUsers();
        } else {
          this.showError(res.data?.message || 'No se pudo eliminar el usuario.');
        }
      } catch (e) {
        console.error('Error al eliminar usuario:', e);
        this.showError('Error al eliminar el usuario.');
      } finally {
        this.saving = false;
      }
    },

    openPasswordDialog(row) {
      this.passwordUserId = row.id;
      this.passwordUserName = row.name;
      this.passwordForm = '';
      this.passwordFormConfirm = '';
      this.passwordDialogVisible = true;
    },

    async resetPassword() {
      if (!this.canResetPassword) return;
      this.saving = true;
      try {
        const res = await axiosInstance.post('/api/admin_user_password', {
          token: localStorage.getItem('token'),
          id: this.passwordUserId,
          password: this.passwordForm,
          password_confirmation: this.passwordFormConfirm,
        });
        if (res.data?.success) {
          this.showSuccess('Contraseña restablecida correctamente.');
          this.passwordDialogVisible = false;
        } else {
          this.showError(res.data?.message || 'No se pudo restablecer la contraseña.');
        }
      } catch (e) {
        console.error('Error al restablecer contraseña:', e);
        this.showError('Error al restablecer la contraseña.');
      } finally {
        this.saving = false;
      }
    },
  },

  async mounted() {
    this.$emit('update-loading', true);
    try {
      await this.fetchUsers();
      await this.loadForm(0);
    } finally {
      this.$emit('update-loading', false);
    }
  },
};
</script>

<style>
@import "@/assets/media-module-base.css";

.page-ind-kpi {
  max-width: 1180px;
  margin: 0 auto;
}

/* ── Hero ── */
.usr-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 16px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%);
  border: 1px solid rgba(147, 197, 253, 0.5);
}
.dark .usr-hero {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  border-color: rgba(59, 130, 246, 0.2);
}
.usr-hero-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-color, #0f172a);
}
.dark .usr-hero-title { color: #e4e4e7; }
.usr-hero-icon { color: #2563eb; }
.usr-hero-subtitle {
  margin: 0;
  font-size: 0.82rem;
  color: #64748b;
  max-width: 520px;
  line-height: 1.45;
}
.dark .usr-hero-subtitle { color: #a1a1aa; }
.usr-hero-stats {
  display: flex;
  gap: 10px;
}
.usr-hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 92px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(147, 197, 253, 0.4);
}
.dark .usr-hero-stat {
  background: rgba(24, 24, 27, 0.7);
  border-color: rgba(59, 130, 246, 0.25);
}
.usr-hero-stat-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1d4ed8;
  line-height: 1.1;
}
.dark .usr-hero-stat-value { color: #93c5fd; }
.usr-hero-stat-label {
  font-size: 0.68rem;
  color: #64748b;
  text-align: center;
}
.dark .usr-hero-stat-label { color: #a1a1aa; }

/* ── Toolbar ── */
.ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.ind-toolbar-left {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  flex: 1;
}
.ind-toolbar-field {
  width: 280px;
  flex-shrink: 0;
}
.ind-toolbar-right {
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
}
.field-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: #64748b;
}
.dark .field-label { color: #a1a1aa; }

.kpi-inline-controls-search-input {
  position: relative;
}
.usr-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}
.dark .usr-search-icon { color: #71717a; }
.usr-search-input.p-inputtext {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  padding-left: 2rem;
  font-size: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
}
.usr-search-input.p-inputtext:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}
.dark .usr-search-input.p-inputtext {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #e4e4e7;
}

/* ── Filtros ── */
.usr-filters {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding-bottom: 4px;
}
.usr-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}
.usr-filter-chip:hover { transform: translateY(-1px); }
.usr-filter-chip.is-active {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(219, 234, 254, 0.9);
  color: #1d4ed8;
}
.dark .usr-filter-chip {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #d4d4d8;
}
.dark .usr-filter-chip.is-active {
  border-color: rgba(96, 165, 250, 0.75);
  background: rgba(30, 58, 138, 0.3);
  color: #dbeafe;
}
.usr-filter-count {
  font-size: 0.65rem;
  font-weight: 700;
  opacity: 0.6;
  min-width: 14px;
  text-align: center;
}

/* ── Content fade-in ── */
.kpi-page-content {
  animation: kpi-content-fade-in 0.35s ease-out;
}
@keyframes kpi-content-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.kpi-skel-fade-in { animation: kpi-skel-fade-in 0.4s ease-out; }
@keyframes kpi-skel-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Skeleton ── */
.etq-skel { display: flex; flex-direction: column; gap: 10px; padding: 4px 0; }
.etq-skel-row { display: flex; gap: 12px; height: 20px; }
.etq-skel-cell {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: etq-shimmer 1.5s ease-in-out infinite;
}
.dark .etq-skel-cell {
  background: linear-gradient(90deg, rgba(39, 39, 42, 0.8) 25%, rgba(63, 63, 70, 0.5) 50%, rgba(39, 39, 42, 0.8) 75%);
  background-size: 200% 100%;
}
@keyframes etq-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty ── */
.etq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}
.etq-empty-icon { font-size: 3rem; color: #cbd5e1; margin-bottom: 16px; }
.etq-empty-icon--sm { font-size: 2.2rem; }
.dark .etq-empty-icon { color: #52525b; }
.etq-empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color, #1e293b);
  margin: 0 0 6px;
}
.dark .etq-empty-title { color: #e4e4e7; }
.etq-empty-subtitle {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0 0 20px;
  max-width: 380px;
  line-height: 1.4;
}
.dark .etq-empty-subtitle { color: #71717a; }

/* ── Section title ── */
.etq-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text-color, #1e293b);
}
.dark .etq-section-title { color: #e4e4e7; }
.etq-section-icon { font-size: 0.92rem; color: #3b82f6; opacity: 0.85; }
.etq-table-count {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 3px 0 10px;
  line-height: 1;
  font-size: 0.74rem;
  color: #94a3b8;
  opacity: 0.72;
}
.dark .etq-table-count { color: #71717a; }

/* ── Tabla ── */
.kpi-spots-table .p-datatable-thead > tr > th {
  background: #f1f5f9;
  color: #334155;
  border-color: rgba(148, 163, 184, 0.45);
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.6rem 0.85rem !important;
}
.dark .kpi-spots-table .p-datatable-thead > tr > th {
  background: rgba(24, 24, 27, 0.9);
  color: #d4d4d8;
  border-color: rgba(82, 82, 91, 0.65);
}
.kpi-spots-table .p-datatable-tbody > tr > td {
  border-color: rgba(203, 213, 225, 0.85);
  padding: 0.5rem 0.85rem !important;
  font-size: 0.82rem !important;
}
.dark .kpi-spots-table .p-datatable-tbody > tr > td {
  border-color: rgba(82, 82, 91, 0.35);
}
.kpi-spots-table .p-datatable-tbody > tr:hover > td {
  background: rgba(59, 130, 246, 0.08) !important;
  cursor: pointer;
}
.dark .kpi-spots-table .p-datatable-tbody > tr:hover > td {
  background: rgba(59, 130, 246, 0.06) !important;
}
.kpi-spots-table .p-paginator {
  padding: 0.4rem;
  border-top: 1px solid rgba(203, 213, 225, 0.85);
  background: transparent;
}

/* ── Celdas ── */
.usr-cell-name {
  display: flex;
  align-items: center;
  gap: 10px;
}
.usr-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}
.usr-name {
  font-weight: 600;
  color: var(--text-color, #1e293b);
  line-height: 1.25;
}
.dark .usr-name { color: #e4e4e7; }
.usr-username {
  font-size: 0.74rem;
  color: #94a3b8;
  font-family: 'SF Mono', 'Fira Code', monospace;
}
.dark .usr-username { color: #71717a; }

.usr-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}
.usr-badge-admin {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}
.dark .usr-badge-admin { background: rgba(239, 68, 68, 0.18); color: #fca5a5; }
.usr-badge-panel {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}
.dark .usr-badge-panel { background: rgba(59, 130, 246, 0.18); color: #bfdbfe; }
.usr-badge-booth {
  background: rgba(245, 158, 11, 0.14);
  color: #b45309;
}
.dark .usr-badge-booth { background: rgba(245, 158, 11, 0.18); color: #fcd34d; }
.usr-badge-external {
  background: rgba(148, 163, 184, 0.16);
  color: #64748b;
}
.dark .usr-badge-external { background: rgba(148, 163, 184, 0.2); color: #cbd5e1; }

.usr-perm-count {
  font-weight: 700;
  color: var(--text-color, #1e293b);
}
.dark .usr-perm-count { color: #e4e4e7; }
.usr-perm-label {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-left: 3px;
}
.etq-cell-date { font-size: 0.78rem; color: #71717a; }
.dark .etq-cell-date { color: #a1a1aa; }
.etq-cell-actions { display: flex; gap: 2px; justify-content: center; }
.etq-btn-icon { width: 28px !important; height: 28px !important; }
.etq-btn-edit { color: #3b82f6 !important; }
.etq-btn-delete { color: #ef4444 !important; }
.usr-btn-key { color: #f59e0b !important; }

/* ── Sidebar / Formulario ── */
/* PrimeVue inyecta `.p-sidebar-right .p-sidebar { width: 20rem }` en runtime desde
   el vendor-chunk, por lo que aparece DESPUÉS de este CSS y con especificidad
   empatada (0-2-0) siempre ganaba. Se usa !important para asegurar el ancho. */
.usr-sidebar.p-sidebar {
  width: 880px !important;
  max-width: 96vw !important;
  background: #f8fafc;
}
.dark .usr-sidebar.p-sidebar { background: #18181b; }
.usr-sidebar.p-sidebar .p-sidebar-content {
  padding: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
}
.usr-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}
.usr-form-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 26px 14px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.3);
  flex-shrink: 0;
}
.dark .usr-form-header { border-bottom-color: rgba(82, 82, 91, 0.5); }
.usr-form-title {
  margin: 0 0 3px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-color, #0f172a);
}
.dark .usr-form-title { color: #e4e4e7; }
.usr-form-subtitle {
  margin: 0;
  font-size: 0.78rem;
  color: #64748b;
}
.dark .usr-form-subtitle { color: #a1a1aa; }
.usr-form-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.usr-form-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}
.usr-form-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 26px;
}
.usr-form-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.3);
}
.usr-form-section:last-child { border-bottom: 0; margin-bottom: 8px; }
.usr-form-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.usr-form-section-head .usr-form-section-title { margin-bottom: 0; }
.usr-form-section-title {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 16px;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text-color, #1e293b);
}
.dark .usr-form-section-title { color: #e4e4e7; }
.usr-form-section-title i { color: #3b82f6; }
.usr-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 22px;
}
.usr-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.usr-field.wide { grid-column: 1 / -1; }
.usr-field-inline {
  flex-direction: row;
  align-items: center;
  gap: 10px;
}
.usr-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color, #1e293b);
}
.dark .usr-label { color: #d4d4d8; }
.usr-optional {
  font-weight: 400;
  color: #94a3b8;
  font-size: 0.72rem;
}
.usr-label-inline {
  font-size: 0.82rem;
  color: var(--text-color, #1e293b);
  line-height: 1.35;
}
.dark .usr-label-inline { color: #d4d4d8; }
.usr-hint {
  margin: 2px 0 0;
  font-size: 0.73rem;
  color: #94a3b8;
  line-height: 1.4;
}
.dark .usr-hint { color: #71717a; }

/* ── Permisos ── */
.usr-perm-search {
  position: relative;
  width: 220px;
}
.usr-perm-search i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.72rem;
  color: #94a3b8;
  z-index: 1;
}
.usr-perm-search-input.p-inputtext {
  width: 100%;
  height: 34px;
  border-radius: 8px;
  padding-left: 1.9rem;
  font-size: 0.8rem;
}
.usr-perm-group {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.55);
}
.dark .usr-perm-group {
  border-color: rgba(82, 82, 91, 0.5);
  background: rgba(24, 24, 27, 0.5);
}
.usr-perm-group.is-locked {
  opacity: 0.72;
}
.usr-perm-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.usr-perm-group-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
}
.dark .usr-perm-group-title { color: #a1a1aa; }
.usr-perm-group-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.usr-perm-group-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
}
.dark .usr-perm-group-count { color: #a1a1aa; }
.usr-link {
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.usr-link:hover { text-decoration: underline; }
.usr-link:disabled { color: #cbd5e1; cursor: not-allowed; }
.dark .usr-link { color: #60a5fa; }
.dark .usr-link:hover { color: #93c5fd; }
.dark .usr-link:disabled { color: #52525b; }
.usr-perm-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
}
.usr-perm-item {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.82rem;
  line-height: 1.35;
  color: var(--text-color, #1e293b);
  cursor: pointer;
  padding: 3px 0;
}
.dark .usr-perm-item { color: #d4d4d8; }
.usr-perm-item .p-checkbox,
.usr-booth-item .p-checkbox { flex-shrink: 0; }
/* Checkbox sin marcar legible sobre la superficie oscura */
.dark .usr-perm-group .p-checkbox .p-checkbox-box,
.dark .usr-booth-grid .p-checkbox .p-checkbox-box {
  background: #27272a;
  border-color: #52525b;
}
.usr-perm-label-text {
  line-height: 1.3;
}

/* ── Cabina ── */
.usr-booth-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.usr-booth-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.usr-booth-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 14px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.5);
}
.dark .usr-booth-grid {
  border-color: rgba(82, 82, 91, 0.5);
  background: rgba(24, 24, 27, 0.5);
}
.usr-booth-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.76rem;
  color: var(--text-color, #1e293b);
  cursor: pointer;
}
.dark .usr-booth-item { color: #d4d4d8; }
.usr-booth-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Footer ── */
.usr-form-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 26px;
  border-top: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}
.dark .usr-form-footer {
  border-top-color: rgba(82, 82, 91, 0.5);
  background: rgba(24, 24, 27, 0.6);
}
.usr-form-footer-spacer { flex: 1; }

/* ── Dialog ── */
.etq-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 6px 0;
}
.etq-dialog-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.etq-dialog-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-color, #1e293b);
}
.dark .etq-dialog-label { color: #d4d4d8; }

/* ── Responsive ── */
@media (max-width: 1180px) {
  .usr-sidebar.p-sidebar { width: 760px !important; }
}
@media (max-width: 900px) {
  .usr-hero { flex-direction: column; align-items: flex-start; }
  .usr-hero-stats { width: 100%; }
  .usr-hero-stat { flex: 1; }
}
@media (max-width: 780px) {
  .usr-sidebar.p-sidebar { width: 96vw !important; }
  .usr-form-grid { grid-template-columns: 1fr; }
  .usr-perm-grid { grid-template-columns: 1fr; }
  .usr-booth-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .ind-toolbar { flex-direction: column; align-items: stretch; }
  .ind-toolbar-left { flex-direction: column; align-items: stretch; }
  .ind-toolbar-field { width: 100%; }
  .usr-perm-search { width: 100%; }
  .usr-form-body { padding: 16px 16px; }
  .usr-form-header,
  .usr-form-footer { padding-left: 16px; padding-right: 16px; }
  .usr-booth-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
